import React, { useState, useEffect, useRef } from 'react';
import { Package, AlertCircle, CheckCircle, RefreshCw, Search, X, Check, ArrowRight, ChevronDown, ChevronRight, Truck } from 'lucide-react';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import { POrderLineWithHeaderDto } from '../../../../types/purchaseOrderLines';
import { StcTransDto, stcTransService, CreateStcTransRequest } from '../../../../services/stcTransService';
import { userProfileService } from '../../../../services/userProfileService';
import { UserProfileDto } from '../../../../types/userProfile';
import { BackButton } from '../../../common/BackButton';
import { ReceivingActiveLine } from './ReceivingActiveLine';
import { DirectReceiptModal } from './DirectReceiptModal';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { processBarcodeInput, buildStcTransUpdateRequest, updateTransactionWithFallback } from './helpers';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';

interface OpenReceivingProps {
    onBack: () => void;
    onNavigateToPutAway?: (stcOrderNo: number) => void;
    /** Modus 4: Bilmottak - pre-created BatchID for multi-operator truck receiving */
    bilmottakBatchId?: string;
    /** Modus 4: Session name (e.g., "Morgenleveranse") */
    bilmottakName?: string;
    /** Modus 4: Warehouse for the bilmottak session */
    bilmottakWarehouse?: string;
}

interface ScannedBatchInfo {
    batchNo?: string;
    expDate?: string;
    quantity?: number;
}

/**
 * OpenReceiving - Modus 2: Åpent mottak / Modus 4: Bilmottak
 *
 * Operatør starter mottak uten å velge en spesifikk innkjøpsordre.
 * Skanning søker på tvers av ALLE åpne PO-linjer.
 * - 1 treff: Auto-velg linjen
 * - Flere treff: Vis modal for valg
 * - 0 treff: Vis feilmelding
 *
 * Modus 4 (Bilmottak): Samme funksjonalitet, men med forhåndsopprettet BatchID
 * for multi-operatør støtte. Flere operatører kan jobbe på samme mottak samtidig.
 */
export const OpenReceiving: React.FC<OpenReceivingProps> = ({
    onBack,
    onNavigateToPutAway,
    bilmottakBatchId,
    bilmottakName,
    bilmottakWarehouse
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { currentWarehouse } = useWarehouse();

    // Determine if we're in bilmottak mode
    const isBilmottakMode = !!bilmottakBatchId;

    // State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Use bilmottakBatchId if provided (Modus 4), otherwise empty (Modus 2 will generate)
    const [batchId, setBatchId] = useState<string>(bilmottakBatchId || '');
    const [receivedTransactions, setReceivedTransactions] = useState<StcTransDto[]>([]);
    const [selectedLine, setSelectedLine] = useState<POrderLineWithHeaderDto | null>(null);
    const [matchingLines, setMatchingLines] = useState<POrderLineWithHeaderDto[]>([]);
    const [showLineSelector, setShowLineSelector] = useState(false);
    const [scannedBatchInfo, setScannedBatchInfo] = useState<ScannedBatchInfo | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfileDto | null>(null);
    const [canFinishReceiving, setCanFinishReceiving] = useState(false);
    const [isFinishingReceiving, setIsFinishingReceiving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // State for editing existing transaction
    const [editingTransaction, setEditingTransaction] = useState<StcTransDto | null>(null);

    // State for direct receipt modal
    const [showDirectReceiptModal, setShowDirectReceiptModal] = useState(false);
    const [selectedStcTransForDirectReceipt, setSelectedStcTransForDirectReceipt] = useState<StcTransDto | null>(null);

    // State for showing completed receipts section
    const [showCompletedReceipts, setShowCompletedReceipts] = useState(false);

    const searchInputRef = useRef<HTMLInputElement>(null);

    // For bilmottak mode, filter to show only current operator's transactions
    // This ensures multi-operator support where each operator sees only their own lines
    const myTransactions = isBilmottakMode && user?.operatorId
        ? receivedTransactions.filter(t => t.operatorID === user.operatorId)
        : receivedTransactions;

    // Separate active and completed transactions (from my filtered list)
    const activeTransactions = myTransactions.filter(t => (t.lineStatus ?? 0) < 50);
    const completedTransactions = myTransactions.filter(t => (t.lineStatus ?? 0) >= 50);

    // Load user profile
    useEffect(() => {
        const loadUserProfile = async () => {
            try {
                const profile = await userProfileService.getCurrentUserProfile();
                setUserProfile(profile);
            } catch (error) {
                console.error('Error loading user profile:', error);
            }
        };
        loadUserProfile();
    }, []);

    // Modus 4: Load existing transactions when joining bilmottak session
    useEffect(() => {
        if (isBilmottakMode && bilmottakBatchId) {
            const loadBilmottakTransactions = async () => {
                try {
                    setLoading(true);
                    console.log('Bilmottak mode: Loading existing transactions for batch:', bilmottakBatchId);
                    const transactions = await stcTransService.getTransactionsByBatch(bilmottakBatchId, true);
                    console.log(`Bilmottak: Loaded ${transactions.length} existing transactions`);
                    setReceivedTransactions(transactions);
                } catch (err) {
                    console.warn('Could not load bilmottak transactions:', err);
                    // Don't show error - session might be new with no transactions yet
                } finally {
                    setLoading(false);
                }
            };
            loadBilmottakTransactions();
        }
    }, [isBilmottakMode, bilmottakBatchId]);

    // Check if we can finish receiving when transactions change
    useEffect(() => {
        if (batchId && receivedTransactions.length > 0) {
            checkCanFinishReceiving();
        } else {
            setCanFinishReceiving(false);
        }
    }, [batchId, receivedTransactions]);

    const checkCanFinishReceiving = async () => {
        if (!batchId) {
            setCanFinishReceiving(false);
            return;
        }

        // KRITISK: For Åpent mottak (Modus 2), tillat alltid avslutning når vi har transaksjoner
        // Backend-valideringen kan feile av ulike årsaker (StcTransOrder ikke opprettet riktig, etc.)
        // men vi vet at operatøren har registrert mottak, så det skal være mulig å avslutte
        if (receivedTransactions.length > 0) {
            console.log('Enabling finish because we have transactions:', receivedTransactions.length);
            setCanFinishReceiving(true);
            return;
        }

        try {
            const result = await stcTransService.canFinishReceiving(batchId);
            console.log('canFinishReceiving result:', result);
            setCanFinishReceiving(result.canFinish);
        } catch (error) {
            console.error('Error checking if receiving can be finished:', error);
            setCanFinishReceiving(false);
        }
    };

    /**
     * Refresh transactions from backend
     */
    const refreshTransactions = async () => {
        if (!batchId) return;

        try {
            setIsRefreshing(true);
            const transactions = await stcTransService.getTransactionsByBatch(batchId, true);
            setReceivedTransactions(transactions);
            await checkCanFinishReceiving();
        } catch (error) {
            console.error('Error refreshing transactions:', error);
            setError(t('warehouse.modules.consolidatedReceiving.couldNotRefreshData'));
        } finally {
            setIsRefreshing(false);
        }
    };

    // Check if global barcode scanning is enabled via system setting
    const barcodeScannerEnabled = useBarcodeScannerEnabled();

    // Barcode scanner hook - handles hardware scanner input
    useSimpleBarcodeScanner({
        enabled: barcodeScannerEnabled && !showLineSelector && !selectedLine && !editingTransaction,
        onScan: (barcode: string) => {
            processBarcodeScan(barcode);
        }
    });

    /**
     * Process barcode scan - search across all open PO lines
     */
    const processBarcodeScan = async (scanInput: string) => {
        const barcodeResult = processBarcodeInput(scanInput);
        if (!barcodeResult) return;

        console.log('Processing barcode:', barcodeResult);
        setIsSearching(true);
        setError(null);

        try {
            const results = await pOrderLineService.searchOpenPOLinesByItem(barcodeResult.itemNo);

            if (results.length === 0) {
                setError(t('warehouse.modules.consolidatedReceiving.noOpenPOLinesFound', { itemNo: barcodeResult.itemNo }));
                setMatchingLines([]);
            } else if (results.length === 1) {
                // Single match - auto-select
                await handleLineSelected(results[0], {
                    quantity: barcodeResult.quantity,
                    batchNo: barcodeResult.batchNo,
                    expDate: barcodeResult.expiryDate
                });
            } else {
                // Multiple matches - show selector
                setMatchingLines(results);
                setShowLineSelector(true);
                // Store barcode info for when user selects a line
                setScannedBatchInfo({
                    batchNo: barcodeResult.batchNo,
                    expDate: barcodeResult.expiryDate,
                    quantity: barcodeResult.quantity
                });
            }
        } catch (err) {
            console.error('Error searching PO lines:', err);
            setError(t('warehouse.modules.consolidatedReceiving.searchError', { error: err instanceof Error ? err.message : t('common.errors.unknown') }));
        } finally {
            setIsSearching(false);
        }
    };

    /**
     * Handle when a PO line is selected (either auto or from modal)
     */
    const handleLineSelected = async (
        line: POrderLineWithHeaderDto,
        barcodeData?: ScannedBatchInfo
    ) => {
        console.log('Line selected:', line);
        setSelectedLine(line);
        setShowLineSelector(false);

        // Store batch info from barcode
        if (barcodeData) {
            setScannedBatchInfo(barcodeData);
        }

        // Get or create BatchID for this receiving session
        // In Modus 4 (bilmottak), we already have a BatchID from props
        if (!batchId) {
            if (isBilmottakMode && bilmottakBatchId) {
                // Modus 4: Use the pre-created BatchID
                setBatchId(bilmottakBatchId);
                console.log('Using bilmottak BatchID:', bilmottakBatchId);
            } else {
                try {
                    // Modus 2: Create a new batch that's not tied to a specific order
                    const newBatchId = await stcTransService.getOrCreateBatchIdForOpenReceiving();
                    setBatchId(newBatchId);
                    console.log('Created new BatchID for open receiving:', newBatchId);
                } catch (err) {
                    console.error('Error creating BatchID:', err);
                    setError(t('warehouse.modules.consolidatedReceiving.couldNotCreateReceiving'));
                }
            }
        }
    };

    /**
     * Handle manual search submit
     */
    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchInput.trim()) {
            processBarcodeScan(searchInput.trim());
            setSearchInput('');
        }
    };

    /**
     * Handle save from ReceivingActiveLine - create StcTrans for the selected line
     */
    const handleSave = async (quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => {
        if (!selectedLine || !batchId || quantity <= 0) {
            setError(t('warehouse.modules.consolidatedReceiving.invalidReceivingData'));
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            // Create the StcTrans request
            const createRequest: CreateStcTransRequest = {
                batchID: batchId,
                orderNo: selectedLine.orderNo,
                lineNo: selectedLine.lineNo,
                itemNo: selectedLine.itemNo,
                quantity: quantity,
                itemBatchNo: batchNo || undefined,
                itemBatchExpDate: expDate ? new Date(expDate) : undefined,
                locationName: receivingLocation || userProfile?.defaultGRBinCode || undefined,
                warehouse: selectedLine.warehouse,  // KRITISK: Må lagres for at lokasjoner skal kunne lastes ved redigering
                lineType: 0,   // KRITISK: LineType = 0 kreves for at QuantityDelivered skal oppdateres
                transType: 10, // Goods Receipt
                lineStatus: 20 // Arrived
            };

            console.log('Creating StcTrans:', createRequest);

            const stcTrans = await stcTransService.createTransaction(batchId, createRequest);

            // Add to received transactions
            setReceivedTransactions(prev => [...prev, stcTrans]);

            // Clear current selection to allow next scan
            setSelectedLine(null);
            setScannedBatchInfo(null);

            // Refresh can finish status
            await checkCanFinishReceiving();

        } catch (err) {
            console.error('Error saving receipt:', err);
            setError(t('warehouse.modules.consolidatedReceiving.couldNotSaveReceipt', { error: err instanceof Error ? err.message : t('common.errors.unknown') }));
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Handle receipt cancelled
     */
    const handleCancelReceipt = () => {
        setSelectedLine(null);
        setScannedBatchInfo(null);
    };

    /**
     * Handle direct receipt from ReceivingActiveLine (new receipt)
     * Creates StcTrans and opens DirectReceiptModal for location selection
     */
    const handleDirectReceipt = async (quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => {
        if (!selectedLine || !batchId || quantity <= 0) {
            setError(t('warehouse.modules.consolidatedReceiving.invalidDirectReceiptData'));
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            // Create the StcTrans request
            const createRequest: CreateStcTransRequest = {
                batchID: batchId,
                orderNo: selectedLine.orderNo,
                lineNo: selectedLine.lineNo,
                itemNo: selectedLine.itemNo,
                quantity: quantity,
                itemBatchNo: batchNo || undefined,
                itemBatchExpDate: expDate ? new Date(expDate) : undefined,
                locationName: receivingLocation || userProfile?.defaultGRBinCode || undefined,
                warehouse: selectedLine.warehouse,
                lineType: 0,   // KRITISK: LineType = 0 kreves for at QuantityDelivered skal oppdateres
                transType: 10, // Goods Receipt
                lineStatus: 20 // Arrived
            };

            console.log('Creating StcTrans for direct receipt:', createRequest);
            const createdStcTrans = await stcTransService.createTransaction(batchId, createRequest);

            // Add to received transactions
            setReceivedTransactions(prev => [...prev, createdStcTrans]);

            // Clear current selection
            setSelectedLine(null);
            setScannedBatchInfo(null);

            // Open DirectReceiptModal with the created transaction
            setSelectedStcTransForDirectReceipt(createdStcTrans);
            setShowDirectReceiptModal(true);

        } catch (err) {
            console.error('Error creating StcTrans for direct receipt:', err);
            setError(t('warehouse.modules.consolidatedReceiving.couldNotCreateDirectReceipt', { error: err instanceof Error ? err.message : t('common.errors.unknown') }));
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Handle direct receipt when editing existing transaction
     * Saves changes and opens DirectReceiptModal for location selection
     */
    const handleEditDirectReceipt = async (quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => {
        if (!editingTransaction || !batchId) {
            setError(t('warehouse.modules.consolidatedReceiving.invalidDirectReceiptData'));
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            // Convert expDate string to Date if provided
            const expDateAsDate = expDate ? new Date(expDate) : undefined;

            // Build the update request
            const updateRequest = buildStcTransUpdateRequest(editingTransaction, {
                quantity,
                itemBatchNo: batchNo,
                itemBatchExpDate: expDateAsDate,
                locationName: receivingLocation
            });

            // Update using fallback method
            await updateTransactionWithFallback(
                stcTransService,
                batchId,
                editingTransaction.lineID,
                updateRequest
            );

            // Refresh transactions to get updated data
            await refreshTransactions();

            // Close edit mode
            setEditingTransaction(null);

            // Open DirectReceiptModal with the (updated) transaction
            // Use the editingTransaction with updated values
            const updatedTrans: StcTransDto = {
                ...editingTransaction,
                quantity: quantity,
                itemBatchNo: batchNo,
                itemBatchExpDate: expDateAsDate,
                locationName: receivingLocation || editingTransaction.locationName
            };
            setSelectedStcTransForDirectReceipt(updatedTrans);
            setShowDirectReceiptModal(true);

        } catch (err) {
            console.error('Error updating transaction for direct receipt:', err);
            setError(t('warehouse.modules.consolidatedReceiving.couldNotUpdateDirectReceipt', { error: err instanceof Error ? err.message : t('common.errors.unknown') }));
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Handle DirectReceiptModal close
     */
    const handleDirectReceiptModalClose = () => {
        setShowDirectReceiptModal(false);
        setSelectedStcTransForDirectReceipt(null);
    };

    /**
     * Handle DirectReceiptModal success
     */
    const handleDirectReceiptModalSuccess = async () => {
        await refreshTransactions();
        await checkCanFinishReceiving();
        handleDirectReceiptModalClose();
    };

    /**
     * Handle click on existing transaction to edit it
     */
    const handleTransactionClick = (transaction: StcTransDto) => {
        console.log('Opening transaction for editing:', transaction);
        setEditingTransaction(transaction);
    };

    /**
     * Handle save when editing existing transaction
     */
    const handleEditSave = async (quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => {
        if (!editingTransaction || !batchId) {
            setError(t('warehouse.modules.consolidatedReceiving.invalidUpdateData'));
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            // Convert expDate string to Date if provided
            const expDateAsDate = expDate ? new Date(expDate) : undefined;

            // Build the update request using helper
            const updateRequest = buildStcTransUpdateRequest(editingTransaction, {
                quantity,
                itemBatchNo: batchNo,
                itemBatchExpDate: expDateAsDate,
                locationName: receivingLocation
            });

            // Update using fallback method (tries PATCH, falls back to PUT)
            await updateTransactionWithFallback(
                stcTransService,
                batchId,
                editingTransaction.lineID,
                updateRequest
            );

            // Refresh transactions from backend to get updated data
            await refreshTransactions();

            // Close edit mode
            setEditingTransaction(null);

            // Refresh can finish status
            await checkCanFinishReceiving();

        } catch (err) {
            console.error('Error updating transaction:', err);
            setError(t('warehouse.modules.consolidatedReceiving.couldNotUpdateReceipt', { error: err instanceof Error ? err.message : t('common.errors.unknown') }));
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Handle cancel when editing existing transaction
     */
    const handleEditCancel = () => {
        setEditingTransaction(null);
    };

    /**
     * Handle delete of existing transaction
     */
    const handleEditDelete = async () => {
        if (!editingTransaction || !batchId) return;

        if (!window.confirm(t('warehouse.modules.consolidatedReceiving.confirmDeleteLine'))) {
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            await stcTransService.deleteTransaction(batchId, editingTransaction.lineID);

            // Remove from local list
            setReceivedTransactions(prev =>
                prev.filter(t => t.id !== editingTransaction.id)
            );

            // Close edit mode
            setEditingTransaction(null);

            // Refresh can finish status
            await checkCanFinishReceiving();

        } catch (err) {
            console.error('Error deleting transaction:', err);
            setError(t('warehouse.modules.consolidatedReceiving.couldNotDeleteReceipt', { error: err instanceof Error ? err.message : t('common.errors.unknown') }));
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Finish receiving only - uses operator-specific finish to allow multi-operator
     */
    const handleFinishReceiving = async () => {
        if (!batchId) return;

        try {
            setIsFinishingReceiving(true);
            setError(null);

            // Use the operator-specific finish endpoint for multi-operator support
            const result = await stcTransService.finishOperatorReceiving(batchId);

            if (result.success) {
                setShowSuccessMessage(true);

                if (result.otherOperatorsHaveActiveLines) {
                    console.log(`Finished ${result.linesFinished} lines. ${result.otherOperatorsActiveLinesCount} lines from other operators remain active.`);
                }

                setTimeout(() => {
                    onBack();
                }, 2000);
            } else {
                setError(result.errorMessage || t('warehouse.modules.consolidatedReceiving.couldNotFinishReceiving'));
            }
        } catch (error) {
            console.error('Error finishing receiving:', error);
            setError(t('warehouse.modules.consolidatedReceiving.couldNotFinishReceivingError', { error: error instanceof Error ? error.message : t('common.errors.unknown') }));
        } finally {
            setIsFinishingReceiving(false);
        }
    };

    /**
     * Finish receiving AND generate put-away tasks
     */
    const handleFinishReceivingAndPutAway = async () => {
        if (!batchId) return;

        try {
            setIsFinishingReceiving(true);
            setError(null);

            // For open receiving (Modus 2), we may have multiple orders
            // Use orderNo 0 to indicate "process all in batch"
            const result = await stcTransService.finishReceivingAndPutAway(batchId, {
                orderNo: 0 // Backend will process all transactions in the batch
            });

            // Backend bulk pre-validation: items missing ItemBinSize configuration
            if (result.itemsMissingBinConfig && result.itemsMissingBinConfig.length > 0) {
                setError(t('warehouse.modules.consolidatedReceiving.itemsMissingBinConfig', {
                    items: result.itemsMissingBinConfig.join(', ')
                }));
                setIsFinishingReceiving(false);
                return;
            }

            if (result.success) {
                setShowSuccessMessage(true);

                // Show messages from backend
                if (result.messages && result.messages.length > 0) {
                    console.log('Process messages:', result.messages);
                }

                // Navigate to put-away module after a short delay
                setTimeout(() => {
                    if (onNavigateToPutAway && result.stcOrderNo) {
                        onNavigateToPutAway(result.stcOrderNo);
                    } else if (onNavigateToPutAway) {
                        // If no stcOrderNo but function exists, navigate to put-away list
                        console.warn('No StcOrderNo received from backend, navigating to put-away list');
                        onNavigateToPutAway(0);
                    } else {
                        onBack();
                    }
                }, 1500);
            } else {
                // Handle partial success or failure with improved error messages
                if (result.receivingFinished && !result.putAwayTasksGenerated) {
                    // Parse messages for item-specific errors
                    const itemErrors = result.messages
                        ?.filter(m => m.includes('No bins found for item') || m.includes('Failed to create put-away reservations'))
                        .map(m => {
                            const match = m.match(/item[:\s]+(\S+)/i);
                            return match ? match[1] : null;
                        })
                        .filter(Boolean) || [];

                    if (itemErrors.length > 0) {
                        setError(t('warehouse.modules.consolidatedReceiving.receivingCompletedPutAwayFailed', { items: itemErrors.join(', ') }));
                    } else {
                        setError(t('warehouse.modules.consolidatedReceiving.receivingCompletedNoPutAway'));
                    }
                } else {
                    setError(result.errorMessage || t('warehouse.modules.consolidatedReceiving.couldNotCompleteOperation'));
                }
            }

        } catch (error) {
            console.error('Error finishing receiving and generating put-away:', error);
            setError(t('warehouse.modules.consolidatedReceiving.couldNotCompleteOperationError', { error: error instanceof Error ? error.message : t('common.errors.unknown') }));
        } finally {
            setIsFinishingReceiving(false);
        }
    };

    /**
     * Map POrderLineWithHeaderDto to ReceivingActiveLine's line prop format
     */
    const mapToReceivingLineFormat = (poLine: POrderLineWithHeaderDto) => {
        // Map batchItemYesNo (boolean) to itemType (number)
        // ItemType: 0 = Standard, 1 = Batch, 4 = Batch with expiry
        // For simplicity, if batchItemYesNo is true, we use itemType 1 (batch required)
        const itemType = poLine.batchItemYesNo ? 1 : 0;

        return {
            itemNo: poLine.itemNo,
            description1: poLine.description1 || '',
            quantity: poLine.quantity || 0,                     // Bestilt
            totalReceived: poLine.quantityDelivered || 0,       // Mottatt (tidligere levert)
            arrivedQuantity: 0,                                 // Ankommet i denne sesjonen (starter på 0)
            remainingQuantity: poLine.remainingQuantity || 0,   // Gjenstår
            itemUnitTxt: poLine.itemUnitTxt || 'stk',
            itemType: itemType
        };
    };

    /**
     * Map StcTransDto to ReceivingActiveLine's line prop format (for editing)
     */
    const mapTransactionToLineFormat = (trans: StcTransDto) => {
        return {
            itemNo: trans.itemNo,
            description1: trans.description1 || '',
            quantity: trans.quantityOrigin || trans.quantity || 0,  // Original ordered quantity
            totalReceived: 0,                                        // Not relevant for edit
            arrivedQuantity: trans.quantity || 0,                    // Current quantity
            remainingQuantity: 0,                                    // Not relevant for edit
            itemUnitTxt: trans.itemUnitTxt || 'stk',
            itemType: trans.itemType
        };
    };

    // Check if we have any transactions (use filtered list for bilmottak mode)
    const hasTransactions = myTransactions.length > 0;

    // Determine if we're in any edit/select mode
    const isInEditMode = selectedLine !== null || editingTransaction !== null;

    return (
        <div className="space-y-4">
            {/* Header with action buttons */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <BackButton onClick={onBack} />
                        <div>
                            {isBilmottakMode ? (
                                <>
                                    <h2 className="text-xl font-semibold text-purple-700 flex items-center gap-2">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-medium">
                                            <Truck className="h-3 w-3 mr-1" />
                                            {t('warehouse.modules.consolidatedReceiving.shortName')}
                                        </span>
                                        {bilmottakName || t('warehouse.modules.consolidatedReceiving.title')}
                                    </h2>
                                    <p className="text-sm text-gray-500">
                                        {bilmottakWarehouse && <span className="mr-2">{t('warehouse.modules.consolidatedReceiving.warehouse')}: {bilmottakWarehouse}</span>}
                                        {t('warehouse.modules.consolidatedReceiving.readyForReceivingDescription')}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h2 className="text-xl font-semibold text-gray-900">{t('warehouse.modules.receipt.shortName')}</h2>
                                    <p className="text-sm text-gray-500">
                                        {t('warehouse.modules.consolidatedReceiving.readyForReceivingDescription')}
                                    </p>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Action buttons - same pattern as POrderReceivingDetail */}
                    <div className="flex items-center gap-2">
                        {/* Refresh button */}
                        {batchId && (
                            <button
                                onClick={refreshTransactions}
                                disabled={isRefreshing}
                                className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                                title={t('common.buttons.refresh')}
                            >
                                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            </button>
                        )}

                        {/* Finish buttons - only show when we have transactions */}
                        {hasTransactions && (
                            <>
                                {/* Avslutt - finish receiving only */}
                                <button
                                    onClick={handleFinishReceiving}
                                    disabled={!canFinishReceiving || isFinishingReceiving}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm ${
                                        canFinishReceiving && !isFinishingReceiving
                                            ? 'bg-green-500 text-white hover:bg-green-600'
                                            : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                    }`}
                                    title={!canFinishReceiving ? t('warehouse.modules.consolidatedReceiving.cannotFinishYet') : t('warehouse.modules.consolidatedReceiving.finishReceiving')}
                                >
                                    {isFinishingReceiving ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Check className="h-4 w-4" />
                                    )}
                                    <span className="hidden sm:inline">
                                        {isFinishingReceiving ? t('warehouse.modules.consolidatedReceiving.finishing') : t('warehouse.modules.receipt.actions.finish')}
                                    </span>
                                </button>

                                {/* Mottak & Plassering - finish and go to put-away */}
                                <button
                                    onClick={handleFinishReceivingAndPutAway}
                                    disabled={!canFinishReceiving || isFinishingReceiving}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm ${
                                        canFinishReceiving && !isFinishingReceiving
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                    }`}
                                    title={!canFinishReceiving ? t('warehouse.modules.consolidatedReceiving.cannotFinishYet') : t('warehouse.modules.consolidatedReceiving.finishAndNavigate')}
                                >
                                    {isFinishingReceiving ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <ArrowRight className="h-4 w-4" />
                                    )}
                                    <span className="hidden sm:inline">
                                        {isFinishingReceiving ? t('warehouse.modules.consolidatedReceiving.processing') : t('warehouse.modules.consolidatedReceiving.receiptAndPutAway')}
                                    </span>
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Batch info if active */}
                {batchId && (
                    <div className={`text-xs ${isBilmottakMode ? 'text-purple-600' : 'text-gray-500'}`}>
                        BatchID: {batchId} | {t('warehouse.modules.consolidatedReceiving.myLines')}: {myTransactions.length}
                        {isBilmottakMode && receivedTransactions.length > myTransactions.length && (
                            <span className="ml-2 text-gray-400">
                                ({t('warehouse.modules.consolidatedReceiving.totalLines')}: {receivedTransactions.length})
                            </span>
                        )}
                        {isBilmottakMode && <span className="ml-2">• {t('warehouse.modules.consolidatedReceiving.multiUserActive')}</span>}
                    </div>
                )}
            </div>

            {/* Success message */}
            {showSuccessMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    {t('warehouse.modules.consolidatedReceiving.receivingCompleted')}
                </div>
            )}

            {/* Error message */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center justify-between">
                    <div className="flex items-center">
                        <AlertCircle className="h-5 w-5 mr-2" />
                        {error}
                    </div>
                    <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
                        <X className="h-5 w-5" />
                    </button>
                </div>
            )}

            {/* Search input for manual entry - hide when editing */}
            {!isInEditMode && (
                <div className="bg-white rounded-lg shadow p-4">
                    <form onSubmit={handleSearchSubmit} className="flex space-x-2">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder={t('warehouse.modules.receipt.placeholders.scanBarcode')}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={isSearching}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isSearching || !searchInput.trim()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isSearching ? (
                                <RefreshCw className="h-5 w-5 animate-spin" />
                            ) : (
                                t('common.buttons.search')
                            )}
                        </button>
                    </form>

                    {/* Mobile barcode capture */}
                    <MobileBarcodeCaptureInput
                        onScan={processBarcodeScan}
                        enabled={barcodeScannerEnabled && !isSearching && !showLineSelector && !isInEditMode}
                    />
                </div>
            )}

            {/* Active receiving line - for NEW receipt */}
            {selectedLine && batchId && (
                <div className="mb-4">
                    {/* Show which PO this line is from */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                        <div className="text-sm text-blue-800">
                            <span className="font-medium">{t('warehouse.modules.consolidatedReceiving.purchaseOrder')}:</span> {selectedLine.orderNoERP || selectedLine.orderNo}
                            {selectedLine.supplierName && (
                                <span className="ml-3">
                                    <span className="font-medium">{t('warehouse.modules.consolidatedReceiving.supplier')}:</span> {selectedLine.supplierName}
                                </span>
                            )}
                        </div>
                    </div>

                    <ReceivingActiveLine
                        line={mapToReceivingLineFormat(selectedLine)}
                        currentQuantity={scannedBatchInfo?.quantity || selectedLine.remainingQuantity || 1}
                        scannedBatchInfo={scannedBatchInfo ? {
                            batchNo: scannedBatchInfo.batchNo,
                            expDate: scannedBatchInfo.expDate
                        } : undefined}
                        onSave={handleSave}
                        onCancel={handleCancelReceipt}
                        onDirectReceipt={handleDirectReceipt}
                        companyNo={user?.companyNo}
                        warehouseID={selectedLine.warehouse || userProfile?.defaultWarehouse}
                        defaultReceivingLocation={userProfile?.defaultGRBinCode}
                        showPrintSection={true}
                    />
                </div>
            )}

            {/* Editing existing transaction */}
            {editingTransaction && batchId && (
                <div className="mb-4">
                    {/* Show info about what we're editing */}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-2">
                        <div className="text-sm text-orange-800">
                            <span className="font-medium">{t('warehouse.modules.consolidatedReceiving.editingExisting')}:</span> {editingTransaction.itemNo}
                            <span className="ml-3">
                                <span className="font-medium">{t('warehouse.modules.receipt.orderLine')}:</span> {editingTransaction.orderNo}, {editingTransaction.lineNo}
                            </span>
                        </div>
                    </div>

                    <ReceivingActiveLine
                        line={mapTransactionToLineFormat(editingTransaction)}
                        editingStcTrans={{
                            itemBatchNo: editingTransaction.itemBatchNo || '',
                            itemBatchExpDate: editingTransaction.itemBatchExpDate
                                ? new Date(editingTransaction.itemBatchExpDate).toISOString().split('T')[0]
                                : '',
                            quantity: editingTransaction.quantity || 0,
                            itemType: editingTransaction.itemType,
                            locationName: editingTransaction.locationName || undefined
                        }}
                        currentQuantity={editingTransaction.quantity || 0}
                        onSave={handleEditSave}
                        onCancel={handleEditCancel}
                        onDelete={handleEditDelete}
                        onDirectReceipt={handleEditDirectReceipt}
                        companyNo={user?.companyNo}
                        warehouseID={editingTransaction.warehouse || userProfile?.defaultWarehouse}
                        defaultReceivingLocation={editingTransaction.locationName || userProfile?.defaultGRBinCode}
                        showPrintSection={true}
                    />
                </div>
            )}

            {/* Active transactions list - clickable for editing (LineStatus < 50) */}
            {activeTransactions.length > 0 && !isInEditMode && (
                <div className="bg-white rounded-lg shadow">
                    <div className="px-4 py-3 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">
                            {t('warehouse.modules.consolidatedReceiving.receivedLines')} ({activeTransactions.length})
                        </h3>
                        <p className="text-xs text-gray-500">{t('warehouse.modules.consolidatedReceiving.clickToEdit')}</p>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {activeTransactions.map((trans) => (
                            <button
                                key={trans.id}
                                onClick={() => handleTransactionClick(trans)}
                                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                            >
                                <div>
                                    <div className="font-medium text-gray-900">{trans.itemNo}</div>
                                    <div className="text-sm text-gray-500">{trans.description1}</div>
                                    <div className="text-xs text-gray-400">
                                        {t('warehouse.modules.receipt.orderLine')}: {trans.orderNo}, {trans.lineNo}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-semibold text-green-600">
                                        {trans.quantity} {trans.itemUnitTxt}
                                    </div>
                                    <div className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800">
                                        {t('warehouse.modules.consolidatedReceiving.arrived')}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Completed transactions section (LineStatus >= 50) - NOT editable */}
            {completedTransactions.length > 0 && !isInEditMode && (
                <div className="bg-white rounded-lg shadow mt-4">
                    <button
                        onClick={() => setShowCompletedReceipts(!showCompletedReceipts)}
                        className="flex items-center justify-between w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 transition-colors rounded-t-lg"
                    >
                        <div className="flex items-center space-x-2">
                            <Package className="h-5 w-5 text-gray-600" />
                            <span className="font-medium text-gray-700">
                                {t('warehouse.modules.consolidatedReceiving.completedReceipts')} ({completedTransactions.length})
                            </span>
                        </div>
                        {showCompletedReceipts ? (
                            <ChevronDown className="h-5 w-5 text-gray-600" />
                        ) : (
                            <ChevronRight className="h-5 w-5 text-gray-600" />
                        )}
                    </button>

                    {showCompletedReceipts && (
                        <div className="divide-y divide-gray-200">
                            {completedTransactions.map((trans) => (
                                <div
                                    key={trans.id}
                                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50"
                                >
                                    <div>
                                        <div className="font-medium text-gray-700">{trans.itemNo}</div>
                                        <div className="text-sm text-gray-500">{trans.description1}</div>
                                        <div className="text-xs text-gray-400">
                                            {t('warehouse.modules.consolidatedReceiving.order')}: {trans.orderNo}, {t('warehouse.modules.consolidatedReceiving.line')}: {trans.lineNo}
                                            {trans.locationName && ` • ${t('warehouse.modules.consolidatedReceiving.location')}: ${trans.locationName}`}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-semibold text-green-600">
                                            {trans.quantity} {trans.itemUnitTxt}
                                        </div>
                                        <div className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">
                                            {t('warehouse.modules.consolidatedReceiving.received')}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Empty state */}
            {!isInEditMode && receivedTransactions.length === 0 && !isSearching && (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                    <Package className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {t('warehouse.modules.consolidatedReceiving.readyForReceiving')}
                    </h3>
                    <p className="text-gray-500">
                        {t('warehouse.modules.consolidatedReceiving.scanOrTypeInstruction')}
                    </p>
                </div>
            )}

            {/* Line selector modal */}
            {showLineSelector && matchingLines.length > 0 && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-medium text-gray-900">
                                {t('warehouse.modules.consolidatedReceiving.selectPOLine', { count: matchingLines.length })}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowLineSelector(false);
                                    setMatchingLines([]);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <div className="overflow-y-auto max-h-[60vh]">
                            {matchingLines.map((line) => (
                                <button
                                    key={`${line.orderNo}-${line.lineNo}`}
                                    onClick={() => handleLineSelected(line, scannedBatchInfo || undefined)}
                                    className="w-full px-4 py-3 border-b border-gray-100 hover:bg-blue-50 text-left transition-colors"
                                >
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="font-medium text-gray-900">
                                                {line.itemNo} - {line.description1}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {t('warehouse.modules.consolidatedReceiving.order')}: {line.orderNoERP || line.orderNo} |
                                                {t('warehouse.modules.consolidatedReceiving.supplier')}: {line.supplierName || line.supplierNo}
                                            </div>
                                            <div className="text-xs text-gray-400">
                                                {t('warehouse.modules.consolidatedReceiving.ordered')}: {line.quantity} | {t('warehouse.modules.consolidatedReceiving.remaining')}: {line.remainingQuantity}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-lg font-semibold text-blue-600">
                                                {line.remainingQuantity}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {line.itemUnitTxt}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Direct Receipt Modal */}
            {showDirectReceiptModal && selectedStcTransForDirectReceipt && (
                <DirectReceiptModal
                    isOpen={showDirectReceiptModal}
                    onClose={handleDirectReceiptModalClose}
                    stcTrans={selectedStcTransForDirectReceipt}
                    companyNo={user?.companyNo || ''}
                    warehouseID={selectedStcTransForDirectReceipt.warehouse || userProfile?.defaultWarehouse || currentWarehouse || 'MAIN'}
                    onSuccess={handleDirectReceiptModalSuccess}
                />
            )}
        </div>
    );
};
